import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useState } from 'react';

export type Credentials = {
    email: string;
    tempPassword: string;
};

export const CredentialsModal = ({
    open,
    onClose,
    credentials,
}: {
    open: boolean;
    onClose: () => void;
    credentials: Credentials | null;
}) => {
    const [copied, setCopied] = useState<'email' | 'password' | 'both' | null>(null);

    if (!credentials) return null;

    const copy = async (value: string, which: 'email' | 'password' | 'both') => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(which);
            window.setTimeout(() => setCopied(null), 1500);
        } catch {
            // clipboard unavailable in non-secure contexts; the text
            // fields are selectable as a fallback.
        }
    };

    const both = `Email: ${credentials.email}\nTemporary password: ${credentials.tempPassword}`;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Invite credentials</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="warning">
                        Copy and send these credentials to the user out-of-band
                        (email, chat). They will not be shown again. Use
                        "Resend invite" to issue a new temporary password.
                    </Alert>
                    <TextField
                        label="Email"
                        value={credentials.email}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip
                                        title={copied === 'email' ? 'Copied' : 'Copy'}
                                    >
                                        <IconButton
                                            edge="end"
                                            onClick={() => copy(credentials.email, 'email')}
                                            aria-label="Copy email"
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Temporary password"
                        value={credentials.tempPassword}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip
                                        title={copied === 'password' ? 'Copied' : 'Copy'}
                                    >
                                        <IconButton
                                            edge="end"
                                            onClick={() =>
                                                copy(credentials.tempPassword, 'password')
                                            }
                                            aria-label="Copy temporary password"
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => copy(both, 'both')}
                    startIcon={<ContentCopyIcon />}
                >
                    {copied === 'both' ? 'Copied' : 'Copy both'}
                </Button>
                <Button onClick={onClose} variant="contained">
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};
